import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate
} from 'react-router-dom';
import { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import 'App.css';
import { Landing } from 'pages/Landing';
import { Process } from 'pages/Process';
import { Careers } from 'pages/Careers';
import { Nav } from 'components/Nav';
import { Footer } from 'components/Footer';
import { ServiceArea } from 'pages/ServiceArea';
import { Faqs } from 'pages/Faqs';
import { Gallery } from 'pages/Gallery';
import { Contact } from 'pages/Contact';
import { About } from 'pages/About';
import { trackPageView } from './analytics';
import NotFound from 'pages/NotFound';
import CityServiceArea from './pages/CityServiceArea';
import CountyServiceArea from './pages/CountyServiceArea';
import ScrollToTop from './utils/ScrollToTop';
import FeaturedCategory from 'pages/FeaturedCategory';
import FeaturedServices from 'pages/FeaturedServices';
import Login from 'pages/Login';
import AdminDashboard from 'pages/AdminDashboard';
import BlogCreation from 'pages/BlogCreation';
import BlogManagement from 'pages/BlogManagement';
import Blog from 'pages/Blog';
import BlogArticle from 'pages/BlogArticle';
import GalleryManagement from 'pages/GalleryManagement';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

function getBreadcrumbs(pathname) {
  const baseUrl = 'https://oakandstoneremodel.com';
  const crumbs = [{ name: 'Home', url: baseUrl + '/' }];
  if (pathname === '/' || pathname === '') return crumbs;
  const segments = pathname.split('/').filter(Boolean);
  const mapping = {
    gallery: 'Gallery',
    process: 'Process',
    about: 'About',
    faqs: 'FAQs',
    careers: 'Careers',
    'service-area': 'Service Area',
    contact: 'Contact'
  };
  let url = baseUrl;
  segments.forEach((seg, i) => {
    url += '/' + seg;
    let name = mapping[seg] || seg.charAt(0).toUpperCase() + seg.slice(1);
    // Special case for /gallery/:subgallery
    if (segments[0] === 'gallery' && i === 1) {
      name = seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
    crumbs.push({ name, url });
  });
  return crumbs;
}

function BreadcrumbJsonLd({ pathname }) {
  const crumbs = getBreadcrumbs(pathname);
  const itemListElement = crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: c.url
  }));
  return (
    <script type="application/ld+json">
      {JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement
      })}
    </script>
  );
}

function AppContent() {
  const location = useLocation();
  const [, setScrollPosition] = useState(0);

  useEffect(() => {
    // Track page views on route change
    trackPageView(location.pathname);
    console.log('Tracking Page View:', location.pathname);
  }, [location]);

  useEffect(() => {
    // Function to update scroll position
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      <BreadcrumbJsonLd pathname={location.pathname} />
      <Nav role="banner" />
      <main role="main">
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* portfolio */}
          <Route path="/gallery" element={<Navigate to="/gallery/showcase" replace />} />
          <Route path="/gallery/:subgallery" element={<Gallery />} />
          {/* company */}
          <Route path="/process" element={<Process />} />
          <Route path="/about" element={<About />} />
          {/* <Route path="/why-choose-us" element={<WhyChooseUs />} /> */}
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/careers" element={<Careers />} />
          {/* service area */}
          <Route path="/service-area" element={<ServiceArea />} />
          <Route path="/service-area/:county" element={<CountyServiceArea />} />
          <Route
            path="/service-area/:county/:city"
            element={<CityServiceArea />}
          />
          <Route path="/featured/:slug" element={<FeaturedCategory />} />
          <Route path="/featured" element={<FeaturedServices />} />
          {/* contact/login */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/blog-creation" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <BlogCreation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/blog-management" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <BlogManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gallery-management" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <GalleryManagement />
              </ProtectedRoute>
            } 
          />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer role="contentinfo" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Oak & Stone Construction',
          url: 'https://oakandstoneremodel.com/',
          logo: 'https://res.cloudinary.com/dwoubdppz/image/upload/v1751597827/oak-and-stone-remodel/logo/logo.svg',
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+1-801-555-1234',
              contactType: 'customer service',
              areaServed: 'US',
              availableLanguage: ['English']
            }
          ],
          address: {
            '@type': 'PostalAddress',
            streetAddress: '123 Main St',
            addressLocality: 'Tremonton',
            addressRegion: 'UT',
            postalCode: '84337',
            addressCountry: 'US'
          }
        })}
      </script>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
